import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, AlertOctagon, Briefcase, Library, User,
  Sparkles, FileSearch, Command, Shield,
} from 'lucide-react';

/**
 * Desktop-only left sidebar (hidden <1024px; BottomNav covers mobile).
 * Provides top-level nav PLUS quick links to power-user tools.
 */
const PRIMARY = [
  { key: 'home',    label: 'Home',       icon: Home,         path: '/' },
  { key: 'ask',     label: 'Ask Stewie', icon: Sparkles,     path: '/ask-stewie' },
  { key: 'issues',  label: 'Issues',     icon: AlertOctagon, path: '/report-issue' },
  { key: 'cases',   label: 'Cases',      icon: Briefcase,    path: '/case-locker' },
  { key: 'library', label: 'Library',    icon: Library,      path: '/library' },
];

const TOOLS = [
  { key: 'fact',    label: 'Fact Finder',         icon: FileSearch, path: '/fact-finder' },
  { key: 'conduct', label: 'Discipline Predictor', icon: Shield,    path: '/conduct-predictor' },
  { key: 'cc',      label: 'Command Center',      icon: Command,    path: '/command-center' },
];

export default function DesktopSidebar({ user }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  function isActive(it) {
    if (it.path === '/') return pathname === '/';
    return pathname === it.path || pathname.startsWith(it.path + '/');
  }

  return (
    <aside className="dsb-root" data-testid="desktop-sidebar" aria-label="Primary">
      <style>{styles}</style>
      <button
        type="button"
        className="dsb-brand"
        onClick={() => navigate('/')}
        data-testid="sidebar-brand"
        aria-label="AskStewie home"
      >
        <img src="/stewie_avatar.png" alt="" className="dsb-brand-img" />
        <span className="dsb-brand-text">
          <span className="dsb-brand-1">Ask</span>
          <span className="dsb-brand-2">Stewie</span>
        </span>
      </button>

      <nav className="dsb-nav">
        <div className="dsb-eyebrow">NAVIGATE</div>
        {PRIMARY.map((it) => (
          <SidebarItem key={it.key} it={it} active={isActive(it)} onNavigate={navigate} />
        ))}

        <div className="dsb-eyebrow dsb-eyebrow--spaced">TOOLS</div>
        {TOOLS.map((it) => (
          <SidebarItem key={it.key} it={it} active={isActive(it)} onNavigate={navigate} />
        ))}
      </nav>

      <button
        type="button"
        className="dsb-profile"
        onClick={() => navigate('/profile')}
        data-testid="sidebar-profile"
      >
        <span className="dsb-avatar">{(user?.name || user?.email || 'S')[0]?.toUpperCase()}</span>
        <span className="dsb-profile-text">
          <span className="dsb-profile-name">{(user?.name || user?.email || 'Member').split(' ')[0]}</span>
          <span className="dsb-profile-sub">{user?.is_admin ? 'Administrator' : 'Member'}</span>
        </span>
        <User size={14} />
      </button>
    </aside>
  );
}

function SidebarItem({ it, active, onNavigate }) {
  const Icon = it.icon;
  return (
    <button
      type="button"
      className={`dsb-item ${active ? 'dsb-item--active' : ''}`}
      onClick={() => onNavigate(it.path)}
      data-testid={`sidebar-${it.key}`}
      aria-current={active ? 'page' : undefined}
    >
      <Icon size={18} strokeWidth={active ? 2.4 : 1.9} />
      <span>{it.label}</span>
      {active && <span className="dsb-bar" aria-hidden="true" />}
    </button>
  );
}

const styles = `
/* Hidden on mobile; visible on desktop ≥1024px */
.dsb-root { display: none; }
@media (min-width: 1024px) {
  .dsb-root {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0; left: 0; bottom: 0;
    width: 232px;
    z-index: 40;
    padding: 18px 12px 16px;
    background: linear-gradient(180deg, rgba(13,27,46,0.92) 0%, rgba(6,17,31,0.92) 100%);
    backdrop-filter: blur(18px) saturate(140%);
    -webkit-backdrop-filter: blur(18px) saturate(140%);
    border-right: 1px solid var(--asds-border, rgba(255,255,255,0.06));
  }
  /* Reserve left gutter for any page that mounts the sidebar */
  body[data-has-sidebar="true"] { padding-left: 232px; }
}

.dsb-brand {
  display: flex; align-items: center; gap: 10px;
  width: 100%;
  padding: 6px 10px 14px;
  background: none; border: none; color: inherit;
  cursor: pointer; text-align: left;
}
.dsb-brand-img {
  width: 32px; height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 1.5px solid rgba(34,211,238,0.55);
  box-shadow: 0 0 12px rgba(34,211,238,0.2);
}
.dsb-brand-text { display: inline-flex; gap: 4px; font-size: 18px; font-weight: 900; letter-spacing: -0.01em; }
.dsb-brand-1 { color: #f1f5f9; }
.dsb-brand-2 { color: #facc15; }

.dsb-nav { flex: 1; display: flex; flex-direction: column; gap: 2px; overflow-y: auto; }
.dsb-eyebrow {
  padding: 12px 12px 6px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.14em;
  color: #64748b; text-transform: uppercase;
}
.dsb-eyebrow--spaced { margin-top: 10px; }

.dsb-item {
  position: relative;
  display: flex; align-items: center; gap: 10px;
  width: 100%;
  padding: 9px 12px;
  border: none; background: none; cursor: pointer;
  color: #94a3b8; font-size: 13.5px; font-weight: 600;
  border-radius: 10px;
  text-align: left;
  transition: color 160ms, background 160ms;
}
.dsb-item:hover { color: #f1f5f9; background: rgba(255,255,255,0.04); }
.dsb-item--active { color: #facc15; background: rgba(250,204,21,0.08); }
.dsb-bar {
  position: absolute; left: 2px; top: 22%; bottom: 22%;
  width: 3px; border-radius: 4px;
  background: #facc15;
  box-shadow: 0 0 10px rgba(250,204,21,0.55);
}

.dsb-profile {
  margin-top: auto;
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px;
  background: rgba(255,255,255,0.03);
  border: 1px solid var(--asds-border, rgba(255,255,255,0.06));
  border-radius: 12px;
  color: #f1f5f9; cursor: pointer;
  transition: border-color 160ms;
}
.dsb-profile:hover { border-color: rgba(34,211,238,0.4); }
.dsb-avatar {
  width: 30px; height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%);
  color: #06111f; font-size: 13px; font-weight: 800;
  display: grid; place-items: center;
  flex-shrink: 0;
}
.dsb-profile-text { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.dsb-profile-name {
  font-size: 13px; font-weight: 700;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.dsb-profile-sub { font-size: 10.5px; color: #64748b; }
`;
